import { useEffect, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider } from '@ircf/ui';
import { ApiError } from '../lib/api/client.js';
import { installAnalyticsLifecycle } from '../lib/analytics.js';
import { SessionProvider } from './session.js';
import { ContentProvider } from './content.js';
import { CapabilityProvider } from './capability.js';
import { SceneBridge } from './sceneBridge.js';

/**
 * Query client configuration.
 *
 * The retry policy is the part worth reading. Retrying a 4xx is pointless - the
 * request was wrong, not unlucky - and retrying a 401 actively harms the user by
 * delaying the redirect to sign-in. So only network faults and 5xx responses are
 * retried, and only twice.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),
      },
      mutations: {
        // A mutation must never be retried automatically: a duplicate booking
        // request or a double credit deduction is far worse than a visible error.
        retry: false,
      },
    },
  });
}

export function AppProviders({ children }: { children: ReactNode }): JSX.Element {
  // Created in state so React Fast Refresh does not discard the cache, and so
  // tests can mount multiple isolated trees.
  const [queryClient] = useState(createQueryClient);

  useEffect(() => installAnalyticsLifecycle(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <CapabilityProvider>
        <SceneBridge>
          <SessionProvider>
            <ContentProvider>
              <ToastProvider>{children}</ToastProvider>
            </ContentProvider>
          </SessionProvider>
        </SceneBridge>
      </CapabilityProvider>
    </QueryClientProvider>
  );
}
